(() => {
const mitoqHomepageScrollGroups = new WeakMap();
class MitoQHomeNavigation extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.setupHomepageScroll();
    const desktop = this.querySelector('[data-desktop-shop]');
    const mobile = this.querySelector('[data-mobile-menu]');
    const summary = (details) => details.querySelector(':scope > summary');
    const sync = (details) => summary(details).setAttribute('aria-expanded', String(details.open));
    if (desktop) {
      let focusWasWithin = false;
      this.addEventListener('focusin', () => { focusWasWithin = true; }, options);
      this.addEventListener('focusout', () => {
        if (innerWidth >= 1280) focusWasWithin = false;
      }, options);
      window.addEventListener('resize', () => {
        if (innerWidth < 1280) {
          // Hiding the desktop roots can blur their focused link before resize fires.
          const focusInside = this.contains(document.activeElement) || (focusWasWithin && document.activeElement === document.body);
          desktop.open = false;
          focusWasWithin = false;
          if (focusInside) this.closest('#header-component')?.querySelector('[data-mobile-menu] > summary')?.focus({ preventScroll: true });
        }
      }, options);
      desktop.addEventListener('toggle', () => sync(desktop), options);
      desktop.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'touch') desktop.open = true;
      }, options);
      desktop.addEventListener('pointerleave', () => {
        if (!desktop.contains(document.activeElement)) desktop.open = false;
      }, options);
      desktop.addEventListener('focusout', () => {
        queueMicrotask(() => {
          if (!desktop.contains(document.activeElement) && !desktop.matches(':hover')) desktop.open = false;
        });
      }, options);
      desktop.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          desktop.open = false;
          summary(desktop).focus();
        } else if (event.key === 'ArrowDown' && event.target === summary(desktop)) {
          event.preventDefault();
          desktop.open = true;
          desktop.querySelector('a')?.focus();
        }
      }, options);
      document.addEventListener('click', (event) => {
        if (!desktop.contains(event.target)) desktop.open = false;
      }, options);
    }
    if (mobile) {
      let focusWasWithin = false;
      mobile.addEventListener('focusin', () => { focusWasWithin = true; }, options);
      mobile.addEventListener('focusout', () => {
        if (innerWidth < 1280) focusWasWithin = false;
      }, options);
      const updateTop = () => {
        const header = this.closest('#header-component');
        if (header) this.style.setProperty('--mitoq-drawer-top', `${header.getBoundingClientRect().bottom}px`);
      };
      this.unlock = () => {
        if (this.locked) {
          document.body.style.overflow = this.previousOverflow;
          const main = document.querySelector('#MainContent');
          if (main) main.inert = this.previousMainInert;
          this.locked = false;
        }
      };
      mobile.addEventListener('toggle', () => {
        sync(mobile);
        if (mobile.open) {
          updateTop();
          if (!this.locked) {
            this.previousOverflow = document.body.style.overflow;
            const main = document.querySelector('#MainContent');
            this.previousMainInert = main?.inert || false;
            if (main) main.inert = true;
            document.body.style.overflow = 'hidden';
            this.locked = true;
          }
        } else {
          this.unlock();
        }
      }, options);
      window.addEventListener('resize', () => {
        if (innerWidth >= 1280) {
          const focusInside = mobile.contains(document.activeElement) || (focusWasWithin && document.activeElement === document.body);
          mobile.open = false;
          focusWasWithin = false;
          if (focusInside) this.closest('#header-component')?.querySelector('[data-desktop-shop] > summary')?.focus({ preventScroll: true });
        } else if (mobile.open) updateTop();
      }, options);
      this.addEventListener('keydown', (event) => {
        if (!mobile.open) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          mobile.open = false;
          summary(mobile).focus();
        } else if (event.key === 'Tab') {
          const focusable = [...mobile.querySelectorAll('summary,a[href],[tabindex="0"]')]
            .filter((element) => element.getClientRects().length && element.checkVisibility());
          const first = focusable[0], last = focusable.at(-1);
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }
      }, options);
    }
  }

  setupHomepageScroll() {
    this.headerScrollCleanup?.();
    this.headerScrollCleanup = undefined;
    const header = this.closest('#header-component');
    const section = header?.closest('.header-section');
    const promo = document.querySelector('#header-group .announcement-bar')?.closest('.shopify-section');
    let home = document.querySelector('.mitoq-home, .mitoq-catalog');
    if (!home || !header || !section || !promo) return;

    const join = (group) => {
      group.clients.add(this);
      this.headerScrollCleanup = () => {
        if (!group.clients.delete(this) || group.clients.size) return;
        group.dispose();
        if (mitoqHomepageScrollGroups.get(header) === group) mitoqHomepageScrollGroups.delete(header);
      };
    };
    const existing = mitoqHomepageScrollGroups.get(header);
    if (existing) { join(existing); return; }

    // Both navigation variants share one header controller and its own lifetime.
    const group = { clients: new Set(), dispose: () => {} };
    mitoqHomepageScrollGroups.set(header, group);
    join(group);
    const controller = new AbortController();
    const options = { signal: controller.signal };
    const homeNames = ['--mitoq-promo-height', '--mitoq-home-header-height', '--mitoq-hero-header-offset', '--mitoq-promo-visual-offset'];
    const homeValues = (element) => homeNames.map(name => ({ name, value: element.style.getPropertyValue(name), priority: element.style.getPropertyPriority(name) }));
    let previousHomeValues = homeValues(home);
    const restoreHome = () => {
      if (!home) return;
      for (const { name, value, priority } of previousHomeValues) {
        if (value) home.style.setProperty(name, value, priority);
        else home.style.removeProperty(name);
      }
    };
    const clearHeader = () => {
      delete section.dataset.mitoqHeaderState;
      for (const name of ['--mitoq-nav-natural-offset', '--mitoq-nav-reveal-top', '--mitoq-nav-hide-top']) section.style.removeProperty(name);
    };
    const scrollContainerMediaQuery = matchMedia('(min-width: 990px)');
    const getScrollContainer = () => scrollContainerMediaQuery.matches
      ? document.querySelector('.page-wrapper') || document.scrollingElement
      : document.scrollingElement;
    const getScrollTop = () => getScrollContainer().scrollTop;
    const getScrollEventTarget = () => scrollContainerMediaQuery.matches ? getScrollContainer() : document;
    let scrollTarget;
    let frame;
    let lastScroll = getScrollTop();
    let state = 'natural';
    let promoHeight = 0;
    let headerHeight = 0;
    let promoTop = 0;
    const render = () => {
      if (!home) return;
      const y = getScrollTop();
      const menuOpen = !!header.querySelector('[data-mobile-menu][open]');
      const keyboardFocus = header.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
      if (y <= promoTop) state = 'natural';
      else if (menuOpen || keyboardFocus) state = 'shown';
      else if (y > promoTop + headerHeight) {
        if (y > lastScroll || state === 'natural') state = 'hidden';
        else if (y < lastScroll) state = 'shown';
      }
      if (state === 'hidden') header.querySelector('[data-desktop-shop][open]')?.removeAttribute('open');
      section.dataset.mitoqHeaderState = state;
      lastScroll = y;
    };
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = undefined; render(); });
    };
    const measure = () => {
      const currentHome = document.querySelector('.mitoq-home, .mitoq-catalog');
      if (currentHome !== home) {
        restoreHome();
        home = currentHome;
        previousHomeValues = home ? homeValues(home) : [];
      }
      if (!home) { clearHeader(); return; }
      promoHeight = promo.getBoundingClientRect().height;
      headerHeight = header.getBoundingClientRect().height;
      promoTop = Math.round(promoHeight);
      home.style.setProperty('--mitoq-promo-height', promoTop + 'px');
      home.style.setProperty('--mitoq-promo-visual-offset', promoHeight + 'px');
      home.style.setProperty('--mitoq-home-header-height', headerHeight + 'px');
      home.style.setProperty('--mitoq-hero-header-offset', Math.round(headerHeight) + 'px');
      section.style.setProperty('--mitoq-nav-natural-offset', (promoTop - promoHeight) + 'px');
      section.style.setProperty('--mitoq-nav-reveal-top', promoTop + 'px');
      section.style.setProperty('--mitoq-nav-hide-top', (promoTop - Math.round(headerHeight)) + 'px');
      render();
    };
    const bindScroll = () => {
      scrollTarget?.removeEventListener('scroll', schedule);
      scrollTarget = getScrollEventTarget();
      scrollTarget.addEventListener('scroll', schedule, { ...options, passive: true });
      lastScroll = getScrollTop();
      measure();
    };
    const sizes = new ResizeObserver(measure);
    const visibility = new MutationObserver(measure);
    const homeTree = new MutationObserver(() => {
      if (document.querySelector('.mitoq-home, .mitoq-catalog') !== home) measure();
    });
    sizes.observe(header);
    sizes.observe(promo);
    visibility.observe(promo, { attributes: true, attributeFilter: ['hidden', 'class'] });
    const main = document.querySelector('#MainContent');
    if (main) homeTree.observe(main, { childList: true, subtree: true });
    window.addEventListener('resize', measure, options);
    scrollContainerMediaQuery.addEventListener('change', bindScroll, options);
    header.addEventListener('focusin', schedule, options);
    header.addEventListener('focusout', schedule, options);
    header.addEventListener('toggle', schedule, { ...options, capture: true });
    group.dispose = () => {
      controller.abort();
      scrollTarget?.removeEventListener('scroll', schedule);
      sizes.disconnect();
      visibility.disconnect();
      homeTree.disconnect();
      if (frame) cancelAnimationFrame(frame);
      restoreHome();
      clearHeader();
    };
    bindScroll();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.headerScrollCleanup?.();
    this.unlock?.();
  }
}
if (!customElements.get('mitoq-home-navigation')) {
  customElements.define('mitoq-home-navigation', MitoQHomeNavigation);
}

})();
